import { ClickoutUrl } from '@/helpers/clickout-helper';
import { useExperiment } from '@/libraries/experiment';
import {
	ExperimentAffiliationPlacement,
	getExperimentAffiliateTag,
} from '@/libraries/experiment/experiment-affiliation';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { toValue, type MaybeRefOrGetter } from '@vueuse/core';

export const PFTR_ELIGIBLE_PROVIDERS = ['atp', 'amp', 'prv'];

export function usePromotedFreeTrialRedesignExp(
	providerShortName: MaybeRefOrGetter<string | undefined>,
	// The redesign (border, CTA styling, row position) only targets the fallback free trial (the
	// "watch similar movies free" case) - callers outside that context can leave this at its default.
	isFallback: MaybeRefOrGetter<boolean> = true
) {
	const { activeVariantWithControlGroup: pftrVariant, experimentInfo } = useExperiment(
		'EXP_PROMOTED_FREE_TRIAL_REDESIGN'
	);

	const isEligibleProvider = computed(() => PFTR_ELIGIBLE_PROVIDERS.includes(toValue(providerShortName) ?? ''));

	// Tracking (affiliate tag) applies to any eligible-provider offer - fallback ft, normal ft, and ro alike.
	const trackingVariant = computed(() => (isEligibleProvider.value ? pftrVariant.value : null));

	// Display variant powers the visual redesign, which is scoped to the fallback free trial only.
	const variant = computed(() => (toValue(isFallback) ? trackingVariant.value : null));

	const { country } = storeToRefs(useLanguageStore());

	function setPromotedFreeTrialRedesignTags(url: ClickoutUrl, placement: ExperimentAffiliationPlacement) {
		if (!trackingVariant.value) return url;

		const affiliationShortId = experimentInfo.value.affiliationShortId as string;

		const tag = getExperimentAffiliateTag({
			affiliationShortId,
			variant: trackingVariant.value,
			country: country.value,
			placement,
			providerShortName: toValue(providerShortName),
		});
		return url.set('uct_ctag', tag);
	}

	return {
		variant,
		setPromotedFreeTrialRedesignTags,
	};
}
